import { useEffect } from "react";
import { isEmpty } from "lodash";
import { v4 as uuidv4, validate as uuidValidate } from "uuid";

import { refillOrderTypeFor } from "~/shared/utils/sharedUtils";
import KickClient from "~/visits/utils/api.js";
import {
  clearOrderComplete,
  clearOrderItems,
  clearConsultAnswersJSON,
  getVisitID,
  getActiveSubscriptionBySubType,
  setBetaBlockerVariant,
  setConsultationTypeOrderItem,
  setOrderItemSubcategory,
  setFollowUpVisitFlag,
  setLatestAddress,
  setUserID,
  setVisitEmail,
  setVisitID,
  setVisitPhone,
  setVisitProduct,
  setVisitDifferentPharmacyJSON,
  setIsPickup,
  setConsultationTypeOrderItemAddon,
  setOriginalRefillOrderItems,
  getOrderItemsList,
  setIsEditingPendingVisitPharmacy,
  clearIsEditingPendingVisitPharmacy,
  clearVisitCookies,
  setUpgradingTo2Pack,
} from "~/visits/utils/visitCookies";

import {
  ATE_SCO,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  SUBSCRIPTION_SUB_TYPE_SLEEP,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
} from "~/shared/sharedConstants";

import { GET_PATIENT_RECORD } from "~/patient-portal/components/portalQueries.js";

export function setProperVisitCookies(
  userID,
  visitID,
  orderType,
  email,
  phone,
  address1,
  address2,
  city,
  usState,
  zip,
  checkoutSessionID
) {
  clearConsultAnswersJSON();
  setVisitProduct(orderType, checkoutSessionID);
  setVisitEmail(email);
  clearOrderComplete(checkoutSessionID);

  if (phone) {
    setVisitPhone(phone.replace(/[-()]/g, "").replace(" ", ""));
  }

  setUserID(userID);
  setVisitID(visitID, checkoutSessionID);
  setLatestAddress({
    address1: address1,
    address2: address2,
    city: city,
    usState: usState,
    zip: zip,
  });
}

export function setCookiesAndRedirectToInsomniaFollowUpVisit(
  { id: userID, email, phone },
  { address1, address2, city, usState, zip, pharmacyInfo, orderType }
) {
  const checkoutSessionID = uuidv4();
  clearOrderItems(checkoutSessionID);
  clearIsEditingPendingVisitPharmacy(checkoutSessionID);
  setProperVisitCookies(
    userID,
    "",
    "",
    email,
    phone,
    address1,
    address2,
    city,
    usState,
    zip,
    checkoutSessionID
  );
  let isVisitOnly = orderType.includes("visit-only");
  const activeSubscription = getActiveSubscriptionBySubType(
    SUBSCRIPTION_SUB_TYPE_SLEEP
  );
  if (activeSubscription) {
    isVisitOnly = activeSubscription.orderType.includes("visit-only");
  }
  if (isVisitOnly) {
    setIsPickup(true);
    if (pharmacyInfo && !isEmpty(pharmacyInfo)) {
      setVisitDifferentPharmacyJSON(pharmacyInfo, checkoutSessionID);
    }
    setVisitProduct(
      ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
      checkoutSessionID
    );
  } else {
    setVisitProduct(
      ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
      checkoutSessionID
    );
  }

  if (!activeSubscription) {
    if (isVisitOnly) {
      setVisitProduct(
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
        checkoutSessionID
      );
    } else {
      setVisitProduct(
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
        checkoutSessionID
      );
    }
  }

  setFollowUpVisitFlag("yes", checkoutSessionID);
  let consultationType = CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP;
  window.location.href = `/visits/${consultationType}/1?checkoutSessionID=${checkoutSessionID}`;
}

const followUpConsultationTypeFor = (consultationType) => {
  switch (consultationType) {
    case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
      return CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;
    case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
      return CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP;
    case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY:
      return CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP;
    default:
      return "";
  }
};

function setRefillOrderItems(orderItems, checkoutSessionID) {
  clearOrderItems(checkoutSessionID);
  const addonOrderItems = [];
  const nonAddonOrderItems = [];
  orderItems.forEach((orderItem) => {
    if (orderItem.isAddon) {
      addonOrderItems.push(orderItem);
    } else {
      nonAddonOrderItems.push(orderItem);
    }
  });

  (nonAddonOrderItems || []).forEach((orderItem) => {
    const { consultationType, productSlug, subcategory } = orderItem;
    const followUpConsultationType =
      followUpConsultationTypeFor(consultationType) || consultationType;
    const refillProductSlug = refillOrderTypeFor(productSlug) || productSlug;
    setConsultationTypeOrderItem(
      followUpConsultationType,
      refillProductSlug,
      checkoutSessionID
    );
    if (subcategory) {
      setOrderItemSubcategory(
        followUpConsultationType,
        subcategory,
        checkoutSessionID
      );
    }
  });

  (addonOrderItems || []).forEach((orderItem) => {
    const { consultationType, productSlug, subcategory } = orderItem;
    const followUpConsultationType =
      followUpConsultationTypeFor(consultationType) || consultationType;
    const refillProductSlug = refillOrderTypeFor(productSlug) || productSlug;
    setConsultationTypeOrderItemAddon(
      followUpConsultationType,
      refillProductSlug,
      subcategory,
      checkoutSessionID
    );
  });

  setOriginalRefillOrderItems(
    getOrderItemsList(checkoutSessionID),
    checkoutSessionID
  );
}

export function setProperVisitCookiesForRefills(
  { id: userID, email, phone, address1, address2, city, usState, zip }, // patient
  { id: visitID, orderType: originalVisitOrderType, orderItems }, // visit
  { medicationName, medicationDosage }, // prescription
  checkoutSessionID
) {
  clearVisitCookies();
  clearConsultAnswersJSON();

  let refillOrderType = refillOrderTypeFor(originalVisitOrderType);
  setBetaBlockerVariant(
    `${medicationName} ${medicationDosage}`,
    checkoutSessionID
  );
  setProperVisitCookies(
    userID,
    visitID,
    refillOrderType,
    email,
    phone,
    address1,
    address2,
    city,
    usState,
    zip,
    checkoutSessionID
  );
  // Older visits may not have orderItems
  if (orderItems?.length) {
    setRefillOrderItems(orderItems, checkoutSessionID);
  }
}

export function setCookiesAndRedirectToPerformanceFollowUpVisit(
  patient,
  prescription,
  productType = null,
  upgradingTo2Pack = false
) {
  const checkoutSessionID = uuidv4();
  setProperVisitCookiesForFollowUp(patient, prescription, checkoutSessionID);
  const consultationType =
    CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;

  if (productType) {
    setVisitProduct(productType, checkoutSessionID);
    setConsultationTypeOrderItem(
      consultationType,
      productType,
      checkoutSessionID
    );
  }

  if (upgradingTo2Pack) {
    setUpgradingTo2Pack(true, checkoutSessionID);
  }

  window.location.href = `/visits/${consultationType}/1?checkoutSessionID=${checkoutSessionID}`;
}

export function setProperVisitCookiesForFollowUp(
  { id: userID, email, phone, address1, address2, city, usState, zip },
  { medicationName, medicationDosage },
  checkoutSessionID
) {
  clearOrderItems(checkoutSessionID);
  clearIsEditingPendingVisitPharmacy(checkoutSessionID);

  setFollowUpVisitFlag("yes", checkoutSessionID);
  setBetaBlockerVariant(
    `${medicationName} ${medicationDosage}`,
    checkoutSessionID
  );
  setProperVisitCookies(
    userID,
    "",
    "",
    email,
    phone,
    address1,
    address2,
    city,
    usState,
    zip,
    checkoutSessionID
  );
}

export function saveConsultIntake(visitID, consultationType, intake) {
  const path = `/visits/${visitID}/consult-intakes`;
  KickClient.newAuthenticatedClient()
    .post(path, {
      consultationType,
      intake,
    })
    .then((response) => {
      console.log(response);
    })
    .catch((error) => {
      console.error("[saveConsultIntake] Error: ", error);
    });
}

export function useVisitIDRequired(checkoutSessionID) {
  let poller;
  useEffect(() => {
    poller = setInterval(() => {
      const visitID = getVisitID(checkoutSessionID);
      if (!visitID) {
        window.location.href = "/me/portal";
        clearInterval(poller);
      }
    }, 1000);
    return () => clearInterval(poller);
  }, [checkoutSessionID]);
}

export function setCookiesAndRedirectToEditPharmacy(visit) {
  const checkoutSessionID = uuidv4();
  setVisitDifferentPharmacyJSON(visit.pharmacyInfo, checkoutSessionID);
  setVisitID(visit.id, checkoutSessionID);
  setIsEditingPendingVisitPharmacy(true, checkoutSessionID);
  window.location.href = `/visits/edit/select-pharmacy?checkoutSessionID=${checkoutSessionID}`;
}

export function ensureCheckoutSessionID(checkoutSessionID, history) {
  // if checkoutSessionID is a uuid, then return it, otherwise create a random one and add it to the query string
  if (uuidValidate(checkoutSessionID)) {
    return true;
  }
  const path = window.location.pathname;
  const newCheckoutSessionID = uuidv4();
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set("checkoutSessionID", newCheckoutSessionID);
  const newPath = path + "?" + searchParams.toString() + window.location.hash;
  // history.push(newPath);
  // need hard reload
  window.location.href = newPath;
  return false;
}

/**
 * Checks if a user should be redirected to a follow-up flow based on their visit history.
 * - For "pac" or "paf" users with completed scopolamine orders, redirects to "gaf"
 * - For initial visit types with completed initial visits, redirects to follow-up
 *
 * @param {Object} apolloClient - Apollo client instance for GraphQL queries
 * @param {string} consultationType - The current consultation type abbreviation
 * @param {string} checkoutSessionID - The checkout session ID
 * @param {Object} options - Optional configuration
 * @param {boolean} options.redirectToPortal - If true, redirects to /me/portal instead of follow-up questionnaire
 * @returns {Promise<boolean>} - Returns true if a redirect was triggered, false otherwise
 */
export async function checkAndRedirectToFollowUpIfNeeded(
  apolloClient,
  consultationType,
  checkoutSessionID,
  options = {}
) {
  const { redirectToPortal = false } = options;

  const redirectConfig = {
    [CONSULTATION_TYPE_ABBREVIATION_INSOMNIA]: {
      visitType: VISIT_TYPE_INSOMNIA,
      followUpPath: CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
    },
    [CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY]: {
      visitType: VISIT_TYPE_PERFORMANCE_ANXIETY,
      followUpPath: CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
    },
  };

  const config = redirectConfig[consultationType];

  // Check if this is a "pac" or "paf" consultation that might need "gaf" redirect
  const isPacOrPaf =
    consultationType === CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY ||
    consultationType === CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;

  // If no config and not "pac" or "paf", nothing to check
  if (!config && !isPacOrPaf) return false;

  try {
    const { data } = await apolloClient.query({
      query: GET_PATIENT_RECORD,
      fetchPolicy: "network-only",
    });

    if (data?.patientRecord?.visits) {
      // Check for "pac" or "paf" users with completed scopolamine orders - redirect to "gaf"
      if (isPacOrPaf) {
        const hasCompletedScopolamineVisit = data.patientRecord.visits.some(
          (visit) =>
            visit.status === DOCTOR_VISIT_STATUS_CLOSED_COMPLETE &&
            visit.orderType?.includes(ATE_SCO)
        );

        if (hasCompletedScopolamineVisit) {
          if (redirectToPortal) {
            window.location.href = "/me/portal";
          } else {
            window.location.href = `/visits/${CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP}/1?checkoutSessionID=${checkoutSessionID}`;
          }
          return true;
        }
      }

      // Check for completed initial visit - redirect to follow-up
      if (config) {
        const hasCompletedInitialVisit = data.patientRecord.visits.some(
          (visit) =>
            visit.visitType === config.visitType &&
            visit.status === DOCTOR_VISIT_STATUS_CLOSED_COMPLETE
        );

        if (hasCompletedInitialVisit) {
          if (redirectToPortal) {
            window.location.href = "/me/portal";
          } else {
            window.location.href = `/visits/${config.followUpPath}/1?checkoutSessionID=${checkoutSessionID}`;
          }
          return true;
        }
      }
    }
  } catch (error) {
    // Log error but don't block the user from continuing
    console.error("Error checking for follow-up redirect:", error);
  }

  return false;
}
